/**
 * PDF reference open helpers — same behavior as DashboardChatbot.jsx
 */

function getIdToken(auth) {
  return (
    auth?.user?.idToken ||
    auth?.idToken ||
    auth?.user?.id_token ||
    (typeof localStorage !== 'undefined' ? localStorage.getItem('id_token') : null)
  );
}

export function buildAuthHeaders(auth) {
  const h = { 'Content-Type': 'application/json' };
  const idTok = getIdToken(auth);
  if (idTok) h.Authorization = `Bearer ${idTok}`;
  return h;
}

export function createPdfReferenceHandlers({ projectId, auth, onOpenViewer }) {
  const PDF_SIGNED_URL_API = process.env.REACT_APP_PDF_SIGNED_URL_API;
  const CODES_API_BASE = process.env.REACT_APP_CODES_API_BASE;

  const openPdfReference = async (ref, docMeta) => {
    try {
      if (!ref?.originalPdfKey) {
        alert('Sorry, I cannot open this reference (missing PDF key).');
        return;
      }
      if (!projectId) {
        alert('No project selected for this reference.');
        return;
      }
      if (!CODES_API_BASE) {
        alert('Codes API is not configured on the frontend (missing REACT_APP_CODES_API_BASE).');
        return;
      }

      const fileId = docMeta?.id || ref.fileId || ref.originalPdfKey;
      const refType = ref.type || null;
      const refKey = ref.value || ref.refKey || null;

      if (!refKey) {
        alert('This reference is missing an identifier.');
        return;
      }

      const qs = new URLSearchParams({
        projectId: String(projectId),
        fileId: String(fileId),
        originalPdfKey: ref.originalPdfKey,
        refKey: String(refKey),
      });
      if (refType) qs.append('refType', refType);

      const url = `${CODES_API_BASE}/codes/resolve?${qs.toString()}`;
      const res = await fetch(url, { method: 'GET', headers: buildAuthHeaders(auth) });
      const rawBody = await res.text();
      let data;
      try {
        data = rawBody ? JSON.parse(rawBody) : {};
      } catch {
        data = { raw: rawBody };
      }

      if (!res.ok) {
        alert(`Could not resolve reference: ${data?.error || res.status}`);
        return;
      }

      const pdfUrl = data.presignedPdfUrl || data.pdfUrl || data.url;
      if (!pdfUrl) {
        alert('Could not open PDF (missing URL in resolve response).');
        return;
      }

      const page =
        typeof data.definitionPage === 'number' && data.definitionPage > 0
          ? data.definitionPage
          : typeof ref.pageNumber === 'number' && ref.pageNumber > 0
            ? ref.pageNumber
            : 1;

      onOpenViewer({
        open: true,
        url: pdfUrl,
        initialPage: page,
        title: docMeta?.title || ref.displayName || ref.fileName || 'PDF Document',
        highlightText: docMeta?.highlightText || data.preview || refKey,
      });
    } catch (err) {
      console.error('openPdfReference:', err);
      alert(`Unexpected error opening PDF: ${err.message}`);
    }
  };

  const openPdfRefPage = async (ref, docMeta, page) => {
    try {
      if (!PDF_SIGNED_URL_API) {
        alert('PDF signed-url API is not configured on the frontend.');
        return;
      }

      const originalPdfKey = ref.originalPdfKey || ref.key;
      if (!originalPdfKey) {
        alert('Sorry, I cannot open this page (no PDF key found).');
        return;
      }

      const res = await fetch(PDF_SIGNED_URL_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ originalPdfKey }),
      });

      const bodyText = await res.text();
      let data;
      try {
        data = bodyText ? JSON.parse(bodyText) : {};
      } catch {
        data = {};
      }

      if (!res.ok) {
        alert(`Could not open reference page: ${data?.error || res.status}`);
        return;
      }

      const pdfUrl = data.url || data.presignedPdfUrl || data.pdfUrl;
      if (!pdfUrl) {
        alert('Could not open PDF (missing URL from signed-url API).');
        return;
      }

      const pageNumber =
        typeof page === 'number' && page > 0
          ? page
          : (Array.isArray(ref.pages) && ref.pages[0]) || 1;

      onOpenViewer({
        open: true,
        url: pdfUrl,
        initialPage: pageNumber,
        title: docMeta?.title || `${ref.type || 'Reference'} ${ref.value || ''}`,
        highlightText: docMeta?.highlightText || ref.value || ref.label || null,
      });
    } catch (err) {
      console.error('openPdfRefPage:', err);
      alert(`Unexpected error opening reference page: ${err.message}`);
    }
  };

  const openPdfPageFromDoc = async (docMeta) => {
    try {
      if (!PDF_SIGNED_URL_API) {
        alert('PDF signed-url API is not configured on the frontend.');
        return;
      }

      const firstRef = Array.isArray(docMeta?.references)
        ? docMeta.references.find((r) => r?.originalPdfKey || r?.key)
        : null;

      const originalPdfKey =
        docMeta?.originalPdfKey || firstRef?.originalPdfKey || firstRef?.key;
      if (!originalPdfKey) {
        alert('Sorry, I cannot open this snippet (no PDF key found).');
        return;
      }

      const res = await fetch(PDF_SIGNED_URL_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ originalPdfKey }),
      });

      const bodyText = await res.text();
      let data;
      try {
        data = bodyText ? JSON.parse(bodyText) : {};
      } catch {
        data = {};
      }

      if (!res.ok) {
        alert(`Could not open snippet page: ${data?.error || res.status}`);
        return;
      }

      const pdfUrl = data.url || data.presignedPdfUrl || data.pdfUrl;
      if (!pdfUrl) {
        alert('Could not open PDF (missing URL from signed-url API).');
        return;
      }

      const pageNum =
        typeof docMeta.pageNumber === 'number' && docMeta.pageNumber > 0
          ? docMeta.pageNumber
          : 1;

      onOpenViewer({
        open: true,
        url: pdfUrl,
        initialPage: pageNum,
        title: docMeta.title || 'PDF Document',
        highlightText: docMeta.highlightText || null,
      });
    } catch (err) {
      console.error('openPdfPageFromDoc:', err);
      alert(`Unexpected error opening snippet page: ${err.message}`);
    }
  };

  const openEvidenceSource = async (sourceBlock, fallbackMeta) => {
    if (!sourceBlock && !fallbackMeta) return;
    const meta = {
      id: sourceBlock?.id || fallbackMeta?.id,
      title: sourceBlock?.docName || sourceBlock?.title || fallbackMeta?.title,
      pageNumber: sourceBlock?.pageNumber ?? fallbackMeta?.pageNumber ?? null,
      originalPdfKey:
        sourceBlock?.originalPdfKey || fallbackMeta?.originalPdfKey || null,
      references: sourceBlock?.references || fallbackMeta?.references || [],
      highlightText:
        sourceBlock?.highlightExcerpt ||
        sourceBlock?.statement ||
        fallbackMeta?.highlightExcerpt ||
        fallbackMeta?.statement ||
        null,  
    };

    const hasAnyPdfKey =
      !!meta.originalPdfKey || (meta.references || []).some((r) => r?.originalPdfKey);
    if (!hasAnyPdfKey) {
      alert('Sorry, I cannot open this document (no PDF key found).');
      return;
    }

    const pageRef = (meta.references || []).find(
      (r) => r?.originalPdfKey && String(r.type || '').toLowerCase() === 'page'
    );
    if (pageRef) {
      const page =
        typeof meta.pageNumber === 'number' && meta.pageNumber > 0
          ? meta.pageNumber
          : pageRef.pageNumber || (Array.isArray(pageRef.pages) ? pageRef.pages[0] : 1);
      await openPdfRefPage(pageRef, meta, page);
      return;
    }

    const sectionRef = (meta.references || []).find((r) => {
      if (!r?.originalPdfKey) return false;
      const t = String(r.type || '').toLowerCase();
      return t && t !== 'page';
    });
    if (sectionRef) {
      await openPdfReference(sectionRef, meta);
      return;
    }

    await openPdfPageFromDoc(meta);
  };

  return { openPdfReference, openPdfRefPage, openPdfPageFromDoc, openEvidenceSource };
}
